import { Helper } from '@/mixin/helper.js'
import WsVoip from './ws.js'
import { float32ToInt16PCM } from './utils.js'
import { alawFromPCM } from './g711a.js'
import { playG711Audio } from './play-audio.js'

export default class Voip {
  constructor(token, vueInstance) {
    this.audioContext = null
    this.sourceNode = null
    this.scriptProcessorNode = null
    this.mediaStream = null
    this.socket = null
    this.token = token
    this.vueInstance = vueInstance
    this.recording = []
    this.recorder = null
    this.wavWorker = null
    this.g711Processor = null
    this.bufferSize = Number(process.env.VUE_APP_VOIP_BUFFER_SIZE ?? 640)
    this.isDeviceMuted = false
    this.lastMessageTimestamp = Date.now()
    this.messageTimeout = 3000
    this.checkMessageInterval = null
    this.isMessageHaveBeenReceived = false
    this.isDeviceBuffering = false
  }
  // Function to start capturing audio
  async startCapture() {
    // eslint-disable-next-line no-useless-catch
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      })

      this.audioContext = new window.AudioContext({
        sampleRate: 8000,
      })

      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream)
      this.socket = new WsVoip()

      await this.socket.connect(`${process.env.VUE_APP_VOIP_WS_URL}/ws?token=` + this.token)

      // Load both worklets
      await this.audioContext.audioWorklet.addModule('/voip/recorder.worklet.js')
      await this.audioContext.audioWorklet.addModule('/voip/g711-processor.js')

      this.recorder = new AudioWorkletNode(this.audioContext, 'recorder.worklet')
      this.g711Processor = new AudioWorkletNode(this.audioContext, 'g711-processor')

      // Connect the audio chain:
      // sourceNode -> recorder -> g711Processor -> destination
      this.sourceNode.connect(this.recorder)
      this.recorder.connect(this.g711Processor)
      this.g711Processor.connect(this.audioContext.destination)

      this.recorder.port.postMessage({ bufferSize: this.bufferSize })

      this.recorder.port.onmessage = (e) => {
        let x = float32ToInt16PCM(e.data)
        const newdata = alawFromPCM(x)
        this.socket.send(newdata)
      }

      this.addEventListenerWebSocket()

      await Helper.methods.delay(2000)
      return true
    } catch (err) {
      this.vueInstance.$mcToast.error(this.vueInstance.$t('global.somethingWentWrong'))
    }
  }

  // Function to stop capturing audio
  stopCapture() {
    this.recording = []
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        if (track.readyState === 'live') {
          track.stop()
        }
      })
    }
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close()
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect()
    }
    if (this.scriptProcessorNode) {
      this.scriptProcessorNode.disconnect()
    }
    if (this.socket) {
      this.socket.close()
    }
    if (this.recorder) {
      this.recorder.disconnect()
    }
  }
  // Then add a method to control mute/unmute
  toggleMuteDevice(isMuted) {
    if (isMuted) {
      this.isDeviceMuted = true
    } else {
      this.isDeviceMuted = false
    }
    if (this.g711Processor) {
      this.g711Processor.port.postMessage({ type: 'mute', value: isMuted })
    }
  }

  adjustVolume(volume) {
    this.g711Processor.port.postMessage({ type: 'gain', value: volume })
  }

  addEventListenerWebSocket() {
    this.socket.socket.onopen = () => {
      // eslint-disable-next-line no-console
      console.log('WebSocket voip connection opened')
      // TODO : make sure this state soon
      // this.vueInstance.$data.isClosed = false
      // this.vueInstance.$data.isOpen = true
      // this.vueInstance.$data.isError = false
    }

    this.socket.socket.onmessage = (event) => {
      const data = event.data
      if (data instanceof Blob) {
        if (!this.isMessageHaveBeenReceived) {
          this.isMessageHaveBeenReceived = true
          this.startMessageCheck()
        }
        if (this.isDeviceBuffering) {
          this.vueInstance.$emit('setCallsValue', this.vueInstance.vehicleId, 'state', 'onGoing')
          this.isDeviceBuffering = false
        }
        this.lastMessageTimestamp = Date.now()
        this.vueInstance.$data.isClosed = false
        this.vueInstance.$data.isOpen = true
        this.vueInstance.$data.isError = false
        if (!this.isDeviceMuted) {
          playG711Audio(data, this.socket.workletNode)
        }
      } else {
        this.vueInstance.$data.isClosed = true
        this.vueInstance.$data.isOpen = false
        this.vueInstance.$data.isError = true
      }
    }

    this.socket.socket.onclose = (event) => {
      // eslint-disable-next-line no-console
      console.log('WebSocket voip connection closed:', event.code, event.reason)
      this.vueInstance.$data.isClosed = true
      this.vueInstance.$data.isOpen = false
      this.vueInstance.$data.isError = false
      this.stopMessageCheck()
      this.isMessageHaveBeenReceived = false
    }

    this.socket.socket.onerror = (error) => {
      // eslint-disable-next-line no-console
      console.error('WebSocket voip error:', error)
      this.vueInstance.$data.isClosed = false
      this.vueInstance.$data.isOpen = false
      this.vueInstance.$data.isError = true
      this.stopMessageCheck()
      this.isMessageHaveBeenReceived = false
      this.vueInstance.$mcToast.error(this.vueInstance.$t('global.somethingWentWrong'))
    }
  }

  startMessageCheck() {
    this.checkMessageInterval = setInterval(() => {
      const timeSinceLastMessage = Date.now() - this.lastMessageTimestamp

      if (timeSinceLastMessage > this.messageTimeout) {
        // No message received for more than 3 seconds
        this.isDeviceBuffering = true
        this.vueInstance.$emit('setCallsValue', this.vueInstance.vehicleId, 'state', 'connecting')
      }
    }, 1000)
  }

  stopMessageCheck() {
    if (this.checkMessageInterval) {
      clearInterval(this.checkMessageInterval)
    }
  }
}
